import React, { useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, ArrowRight, CheckCircle, Info, BarChart3, TrendingUp, GitCompare, Calculator, PieChart, Activity, Target, Layers, ChevronUp, ChevronDown, AlertTriangle, Check } from 'lucide-react';

const B3_QuantitativePlanning = () => {
  const { dataB, updateDataRaw, nextStep, prevStep, logDecision } = useWizard();
  
  const [descriptiveEnabled, setDescriptiveEnabled] = useState(
    dataB?.quantitativeMethods?.descriptiveStats?.enabled || false
  );
  const [selectedDescriptive, setSelectedDescriptive] = useState(
    dataB?.quantitativeMethods?.descriptiveStats?.categories || []
  );
  const [inferentialEnabled, setInferentialEnabled] = useState(
    dataB?.quantitativeMethods?.inferentialTests?.enabled || false
  );
  const [selectedInferential, setSelectedInferential] = useState(
    dataB?.quantitativeMethods?.inferentialTests?.tests || []
  );

  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorMethod, setCalculatorMethod] = useState('cochran');
  const [calcInputs, setCalcInputs] = useState({
    populationSize: '',
    confidenceLevel: 95,
    marginOfError: 5,
    populationProportion: 50
  });
  const [calculatedSize, setCalculatedSize] = useState(null);
  const [currentSampleSize, setCurrentSampleSize] = useState(
    dataB?.sampleSizeCalculator?.calculatedSize || null
  );

  const zScores = { 90: 1.645, 95: 1.96, 99: 2.58 };

  const calculateCochran = () => {
    const N = parseInt(calcInputs.populationSize) || 0;
    const z = zScores[calcInputs.confidenceLevel] || 1.96;
    const p = (calcInputs.populationProportion || 50) / 100;
    const q = 1 - p;
    const e = (calcInputs.marginOfError || 5) / 100;
    
    const n0 = (z * z * p * q) / (e * e);
    
    if (N > 0) {
      const n = n0 / (1 + (n0 - 1) / N);
      return Math.ceil(n);
    }
    return Math.ceil(n0);
  };

  const calculateKrejcieMorgan = () => {
    const N = parseInt(calcInputs.populationSize) || 0;
    if (N <= 0) return null;
    
    const chiSquare = 3.841;
    const P = 0.5;
    const d = 0.05;
    
    const numerator = chiSquare * N * P * (1 - P);
    const denominator = (d * d) * (N - 1) + chiSquare * P * (1 - P);
    
    return Math.ceil(numerator / denominator);
  };

  const calculateSlovin = () => {
    const N = parseInt(calcInputs.populationSize) || 0;
    if (N <= 0) return null;
    
    const e = (calcInputs.marginOfError || 5) / 100;
    const n = N / (1 + N * e * e);
    
    return Math.ceil(n);
  };

  const runCalculation = () => {
    let result = null;
    switch (calculatorMethod) {
      case 'cochran':
        result = calculateCochran();
        break;
      case 'krejcie':
        result = calculateKrejcieMorgan();
        break;
      case 'slovin':
        result = calculateSlovin();
        break;
      default:
        result = null;
    }
    setCalculatedSize(result);
  };

  const applyCalculatedSize = () => {
    if (calculatedSize) {
      setCurrentSampleSize(calculatedSize);
      updateDataRaw({
        'wizardB.sampleSizeCalculator': {
          method: calculatorMethod,
          inputs: calcInputs,
          calculatedSize: calculatedSize
        }
      });
    }
  };

  const calculatorMethods = [
    { 
      value: 'cochran', 
      label: "Cochran's Formula", 
      description: 'Most comprehensive method. Accounts for confidence level, margin of error, and population proportion.',
      formula: 'n = (Z² × p × q) / e²'
    },
    { 
      value: 'krejcie', 
      label: 'Krejcie & Morgan', 
      description: 'Pre-calculated table method using fixed 95% confidence and 5% margin of error.',
      formula: 's = X²NP(1-P) / [d²(N-1) + X²P(1-P)]'
    },
    { 
      value: 'slovin', 
      label: "Slovin's Formula", 
      description: 'Simplest method requiring only population size and margin of error.',
      formula: 'n = N / (1 + N × e²)'
    }
  ];

  const analysisApproach = dataB?.analysisApproach;
  const isQuantOnly = analysisApproach === 'quantitative';

  const descriptiveCategories = [
    {
      id: 'central_tendency',
      icon: Target,
      title: 'Central Tendency',
      description: 'Mean, median, mode',
      dataNeeded: 'Numeric data (ratings, scores, counts)'
    },
    {
      id: 'dispersion',
      icon: Activity,
      title: 'Dispersion & Variability',
      description: 'Standard deviation, variance, range, IQR',
      dataNeeded: 'Numeric data'
    },
    {
      id: 'distribution',
      icon: BarChart3,
      title: 'Distribution Analysis',
      description: 'Skewness, kurtosis, normality tests',
      dataNeeded: 'Numeric data'
    },
    {
      id: 'frequency',
      icon: PieChart,
      title: 'Frequency Distributions',
      description: 'Counts, percentages, cross-tabulations',
      dataNeeded: 'Categorical or numeric data'
    },
    {
      id: 'percentiles',
      icon: Layers,
      title: 'Percentiles & Quartiles',
      description: 'Q1, Q2, Q3, percentile ranks',
      dataNeeded: 'Numeric data'
    },
    {
      id: 'reliability',
      icon: CheckCircle,
      title: 'Reliability Analysis',
      description: "Cronbach's alpha for scale reliability",
      dataNeeded: 'Multiple items measuring same construct'
    },
    {
      id: 'missing_data',
      icon: Calculator,
      title: 'Missing Data Analysis',
      description: 'Missing patterns, MCAR tests',
      dataNeeded: 'Any data type'
    },
    {
      id: 'outliers',
      icon: TrendingUp,
      title: 'Outlier Detection',
      description: 'Z-scores, IQR method, boxplots',
      dataNeeded: 'Numeric data'
    }
  ];

  const inferentialTests = [
    {
      id: 'comparison_2groups',
      icon: GitCompare,
      title: 'Two-Group Comparisons',
      description: 'Independent t-test, paired t-test, Mann-Whitney U, Wilcoxon',
      dataNeeded: 'Numeric DV + 2-level grouping variable',
      examples: 'Compare scores between males vs females, before vs after'
    },
    {
      id: 'comparison_3plus',
      icon: Layers,
      title: 'Multi-Group Comparisons',
      description: 'One-way ANOVA, Kruskal-Wallis, post-hoc tests',
      dataNeeded: 'Numeric DV + 3+ level grouping variable',
      examples: 'Compare satisfaction across 4 departments'
    },
    {
      id: 'correlation',
      icon: TrendingUp,
      title: 'Correlation Analysis',
      description: 'Pearson, Spearman, Kendall correlations',
      dataNeeded: '2+ numeric or ordinal variables',
      examples: 'Relationship between age and satisfaction'
    },
    {
      id: 'regression',
      icon: Activity,
      title: 'Regression Analysis',
      description: 'Linear regression, multiple regression',
      dataNeeded: 'Numeric DV + numeric/categorical predictors',
      examples: 'Predict performance from experience and training'
    },
    {
      id: 'chi_square',
      icon: PieChart,
      title: 'Chi-Square Tests',
      description: 'Independence, goodness of fit',
      dataNeeded: 'Categorical variables',
      examples: 'Association between gender and preference'
    },
    {
      id: 'factorial',
      icon: Calculator,
      title: 'Factorial Designs',
      description: 'Two-way ANOVA, interaction effects',
      dataNeeded: 'Numeric DV + 2+ grouping factors',
      examples: 'Effect of treatment and gender on outcomes'
    },
    {
      id: 'repeated_measures',
      icon: Target,
      title: 'Repeated Measures',
      description: 'Repeated measures ANOVA, Friedman test',
      dataNeeded: 'Same participants measured multiple times',
      examples: 'Scores at baseline, 3 months, 6 months'
    },
    {
      id: 'nonparametric',
      icon: BarChart3,
      title: 'Non-Parametric Alternatives',
      description: 'Mann-Whitney, Kruskal-Wallis, Spearman',
      dataNeeded: 'When normality assumptions violated',
      examples: 'Small samples or skewed distributions'
    }
  ];

  const toggleDescriptive = (id) => {
    setSelectedDescriptive(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleInferential = (id) => {
    setSelectedInferential(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const getTotalSelected = () => {
    return (descriptiveEnabled ? selectedDescriptive.length : 0) + 
           (inferentialEnabled ? selectedInferential.length : 0);
  };

  const handleNext = () => {
    updateDataRaw({
      'wizardB.quantitativeMethods': {
        descriptiveStats: {
          enabled: descriptiveEnabled,
          categories: selectedDescriptive
        },
        inferentialTests: {
          enabled: inferentialEnabled,
          tests: selectedInferential
        }
      }
    });

    const descNames = descriptiveEnabled 
      ? selectedDescriptive.map(id => descriptiveCategories.find(c => c.id === id)?.title).filter(Boolean)
      : [];
    const infNames = inferentialEnabled
      ? selectedInferential.map(id => inferentialTests.find(t => t.id === id)?.title).filter(Boolean)
      : [];

    logDecision(
      'Quantitative Methods',
      `Descriptive: ${descNames.length || 'None'}, Inferential: ${infNames.length || 'None'}`,
      `Descriptive categories: ${descNames.join(', ') || 'None'}. Inferential tests: ${infNames.join(', ') || 'None'}.`
    );

    nextStep();
  };

  const canSkip = !isQuantOnly;
  const hasSelection = getTotalSelected() > 0;

  return (
    <div className="wizard-step">
      <h2>Step 4: Quantitative Analysis Methods</h2>
      <p className="step-description">
        {isQuantOnly
          ? 'Select the quantitative analysis methods you plan to use. Choose at least one category.'
          : 'Select any quantitative analysis methods you want to include. You can skip this if using qualitative only.'
        }
      </p>

      {/* Sample Size Calculator Section */}
      <div className="form-section" style={{ marginBottom: '2rem' }}>
        <div className="sample-size-calculator-toggle">
          <button 
            type="button"
            className="btn btn-secondary calculator-toggle-btn"
            onClick={() => setShowCalculator(!showCalculator)}
          >
            <Calculator size={18} />
            Sample Size Calculator
            {showCalculator ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <small>Use research-based formulas to determine minimum sample size</small>
        </div>

        {showCalculator && (
          <div className="sample-size-calculator">
            <h4>Sample Size Calculator</h4>
            
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <AlertTriangle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.9rem', color: '#92400e' }}>
                <strong>Important:</strong> Using this standard calculator will override any sample size you may have 
                estimated earlier. This provides a scientifically-grounded minimum sample size based on your population 
                parameters and desired confidence level.
              </div>
            </div>

            <p className="helper-text">
              Select a calculation method and enter your parameters to determine the minimum required sample size.
            </p>

            <div className="calculator-method-selection">
              <label>Calculation Method</label>
              <div className="method-cards">
                {calculatorMethods.map(method => (
                  <div 
                    key={method.value}
                    className={`method-card ${calculatorMethod === method.value ? 'selected' : ''}`}
                    onClick={() => {
                      setCalculatorMethod(method.value);
                      setCalculatedSize(null);
                    }}
                  >
                    <input
                      type="radio"
                      name="calculatorMethod"
                      value={method.value}
                      checked={calculatorMethod === method.value}
                      onChange={() => {
                        setCalculatorMethod(method.value);
                        setCalculatedSize(null);
                      }}
                    />
                    <div className="method-content">
                      <strong>{method.label}</strong>
                      <p>{method.description}</p>
                      <code className="formula">{method.formula}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="calculator-inputs">
              <h5>Input Parameters</h5>
              
              <div className="calc-input-row">
                <div className="calc-input-group">
                  <label>Population Size (N) *</label>
                  <input
                    type="number"
                    min="1"
                    value={calcInputs.populationSize}
                    onChange={(e) => setCalcInputs({...calcInputs, populationSize: e.target.value})}
                    placeholder="e.g., 10000"
                  />
                  <small>Total number in your target population</small>
                </div>

                {(calculatorMethod === 'cochran' || calculatorMethod === 'slovin') && (
                  <div className="calc-input-group">
                    <label>Margin of Error (e)</label>
                    <select
                      value={calcInputs.marginOfError}
                      onChange={(e) => setCalcInputs({...calcInputs, marginOfError: parseInt(e.target.value)})}
                    >
                      <option value={1}>1% (very precise)</option>
                      <option value={3}>3% (precise)</option>
                      <option value={5}>5% (standard)</option>
                      <option value={10}>10% (less precise)</option>
                    </select>
                    <small>Acceptable error range (±{calcInputs.marginOfError}%)</small>
                  </div>
                )}

                {calculatorMethod === 'cochran' && (
                  <>
                    <div className="calc-input-group">
                      <label>Confidence Level</label>
                      <select
                        value={calcInputs.confidenceLevel}
                        onChange={(e) => setCalcInputs({...calcInputs, confidenceLevel: parseInt(e.target.value)})}
                      >
                        <option value={90}>90% (Z=1.645)</option>
                        <option value={95}>95% (Z=1.96) - Recommended</option>
                        <option value={99}>99% (Z=2.58)</option>
                      </select>
                      <small>Probability that sample represents population</small>
                    </div>

                    <div className="calc-input-group">
                      <label>Population Proportion (p)</label>
                      <select
                        value={calcInputs.populationProportion}
                        onChange={(e) => setCalcInputs({...calcInputs, populationProportion: parseInt(e.target.value)})}
                      >
                        <option value={50}>50% (conservative/unknown)</option>
                        <option value={30}>30%</option>
                        <option value={20}>20%</option>
                        <option value={10}>10%</option>
                      </select>
                      <small>Expected proportion with attribute (50% = largest sample)</small>
                    </div>
                  </>
                )}

                {calculatorMethod === 'krejcie' && (
                  <div className="calc-input-group info-only">
                    <label>Fixed Parameters</label>
                    <div className="fixed-params">
                      <span>Confidence: 95%</span>
                      <span>Margin of Error: 5%</span>
                      <span>Proportion: 50%</span>
                    </div>
                    <small>Krejcie & Morgan uses standard fixed values</small>
                  </div>
                )}
              </div>

              <button 
                type="button"
                className="btn btn-primary calculate-btn"
                onClick={runCalculation}
                disabled={!calcInputs.populationSize}
              >
                <Calculator size={16} />
                Calculate Minimum Sample Size
              </button>
            </div>

            {calculatedSize && (
              <div className="calculator-result">
                <div className="result-box">
                  <h5>Calculated Minimum Sample Size</h5>
                  <div className="result-value">n = {calculatedSize}</div>
                  <p className="result-method">
                    Using {calculatorMethods.find(m => m.value === calculatorMethod)?.label}
                  </p>
                </div>

                {currentSampleSize && currentSampleSize !== calculatedSize && (
                  <div className={`comparison-notice ${currentSampleSize >= calculatedSize ? 'success' : 'warning'}`}>
                    {currentSampleSize >= calculatedSize ? (
                      <>
                        <Check size={18} />
                        <span>
                          Your saved sample size (N={currentSampleSize}) is adequate. 
                          It exceeds the minimum requirement by {currentSampleSize - calculatedSize}.
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={18} />
                        <span>
                          Your saved sample size (N={currentSampleSize}) is below the calculated minimum. 
                          Consider increasing to at least {calculatedSize}.
                        </span>
                      </>
                    )}
                  </div>
                )}

                <div className="result-actions">
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={applyCalculatedSize}
                  >
                    <Check size={16} />
                    Apply This Sample Size
                  </button>
                  {currentSampleSize === calculatedSize && (
                    <span style={{ color: '#059669', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle size={16} /> Sample size saved
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{
        background: '#ecfeff',
        border: '1px solid #67e8f9',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <Info size={20} color="#0891b2" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.9rem', color: '#0e7490' }}>
          <strong>Data Collection Tip:</strong> Quantitative methods require closed-ended questions with numeric or 
          categorical responses (Likert scales, multiple choice, number inputs). Your selections here will guide 
          question type recommendations in Wizard F.
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div 
          onClick={() => setDescriptiveEnabled(!descriptiveEnabled)}
          style={{
            background: descriptiveEnabled ? '#ecfeff' : '#f8fafc',
            border: `2px solid ${descriptiveEnabled ? '#0891b2' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: `2px solid ${descriptiveEnabled ? '#0891b2' : '#94a3b8'}`,
              background: descriptiveEnabled ? '#0891b2' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {descriptiveEnabled && <CheckCircle size={16} color="white" />}
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Descriptive Statistics</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Summarize and describe your data's characteristics
              </p>
            </div>
          </div>
          <span style={{
            background: descriptiveEnabled ? '#0891b2' : '#e2e8f0',
            color: descriptiveEnabled ? 'white' : '#64748b',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '0.8rem'
          }}>
            {selectedDescriptive.length} selected
          </span>
        </div>

        {descriptiveEnabled && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.75rem',
            marginLeft: '2rem',
            marginBottom: '1.5rem'
          }}>
            {descriptiveCategories.map((cat) => {
              const Icon = cat.icon;
              const isSelected = selectedDescriptive.includes(cat.id);
              return (
                <div
                  key={cat.id}
                  onClick={() => toggleDescriptive(cat.id)}
                  style={{
                    background: isSelected ? '#ecfeff' : 'white',
                    border: `1px solid ${isSelected ? '#0891b2' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <Icon size={16} color={isSelected ? '#0891b2' : '#64748b'} />
                    <span style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>{cat.title}</span>
                    {isSelected && <CheckCircle size={14} color="#0891b2" />}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>{cat.description}</p>
                </div>
              );
            })}
          </div>
        )}

        <div 
          onClick={() => setInferentialEnabled(!inferentialEnabled)}
          style={{
            background: inferentialEnabled ? '#f0f9ff' : '#f8fafc',
            border: `2px solid ${inferentialEnabled ? '#3b82f6' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: `2px solid ${inferentialEnabled ? '#3b82f6' : '#94a3b8'}`,
              background: inferentialEnabled ? '#3b82f6' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {inferentialEnabled && <CheckCircle size={16} color="white" />}
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Inferential Statistics</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Test hypotheses and draw conclusions about populations
              </p>
            </div>
          </div>
          <span style={{
            background: inferentialEnabled ? '#3b82f6' : '#e2e8f0',
            color: inferentialEnabled ? 'white' : '#64748b',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '0.8rem'
          }}>
            {selectedInferential.length} selected
          </span>
        </div>

        {inferentialEnabled && (
          <div style={{
            display: 'grid',
            gap: '0.75rem',
            marginLeft: '2rem'
          }}>
            {inferentialTests.map((test) => {
              const Icon = test.icon;
              const isSelected = selectedInferential.includes(test.id);
              return (
                <div
                  key={test.id}
                  onClick={() => toggleInferential(test.id)}
                  style={{
                    background: isSelected ? '#f0f9ff' : 'white',
                    border: `1px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <Icon size={16} color={isSelected ? '#3b82f6' : '#64748b'} />
                    <span style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>{test.title}</span>
                    {isSelected && <CheckCircle size={14} color="#3b82f6" />}
                  </div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.8rem', color: '#64748b' }}>{test.description}</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                    <strong>Requires:</strong> {test.dataNeeded}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={isQuantOnly && !hasSelection}
        >
          {canSkip && !hasSelection ? 'Skip to Data Requirements' : 'Next Step'}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B3_QuantitativePlanning;
